import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { MapPin, Sparkles, Search, ArrowRight, Search as SearchIcon, MessageSquare, PartyPopper, ShieldCheck, Heart, Image, Palette, Cake } from 'lucide-react';
import api from '../api';
import ServiceCard from '../components/ServiceCard';
import Seo from '../components/Seo';
import './Home.css';

function Home() {
  const navigate = useNavigate();
  const [q, setQ] = useState('');
  const [featured, setFeatured] = useState([]);

  useEffect(() => {
    api
      .get('/servizi', { params: { limit: 6, sort: 'recenti' } })
      .then((res) => setFeatured(res.data.servizi || []))
      .catch(() => setFeatured([]));
  }, []);

  const handleSearch = (e) => {
    e.preventDefault();
    const term = q.trim();
    navigate(`/cerca?tipo=location${term ? `&q=${encodeURIComponent(term)}` : ''}`);
  };

  return (
    <div className="home-page">
      <Seo
        title="Location, animatori e allestimenti per feste di bambini"
        description="Trova e contatta location, animatori e allestimenti per le feste di compleanno dei tuoi bambini in Italia."
        canonical="/"
        jsonLd={[
          {
            '@context': 'https://schema.org',
            '@type': 'WebSite',
            name: 'Feste Da Piccoli',
            url: 'https://festedapiccoli.it',
            potentialAction: {
              '@type': 'SearchAction',
              target: 'https://festedapiccoli.it/cerca?q={search_term_string}',
              'query-input': 'required name=search_term_string',
            },
          },
          {
            '@context': 'https://schema.org',
            '@type': 'Organization',
            name: 'Feste Da Piccoli',
            url: 'https://festedapiccoli.it',
            logo: 'https://festedapiccoli.it/logo.png',
          },
        ]}
      />
      {/* ===== HERO ===== */}
      <section className="hero">
        <div className="hero-inner">
          <div className="hero-content">
            <span className="hero-eyebrow">
              <Heart size={15} /> Feste indimenticabili per i più piccoli
            </span>
            <h1 className="hero-title">
              Trova la festa perfetta<br />per i tuoi bambini
            </h1>
            <p className="hero-subtitle">Location e intrattenimento tutto in un posto.</p>

            <form className="hero-search" onSubmit={handleSearch}>
              <div className="search-field">
                <Search size={20} />
                <input
                  type="text"
                  value={q}
                  onChange={(e) => setQ(e.target.value)}
                  placeholder="Cerca per città, es. Milano…"
                  className="search-input"
                />
              </div>
              <button type="submit" className="home-btn home-btn-primary">
                Cerca
              </button>
            </form>

            <div className="hero-ctas">
              <Link to="/cerca?tipo=location" className="hero-pill">
                <MapPin size={16} /> Location
              </Link>
              <Link to="/cerca?tipo=animazione" className="hero-pill">
                <Sparkles size={16} /> Intrattenimento
              </Link>
              <Link to="/cerca?tipo=allestimenti" className="hero-pill">
                <Palette size={16} /> Allestimenti
              </Link>
              <Link to="/cerca?tipo=food" className="hero-pill">
                <Cake size={16} /> Food
              </Link>
            </div>
          </div>

          <div className="hero-art">
            <img src="/logo-balloon.png" alt="Feste Da Piccoli" className="hero-logo" />
          </div>
        </div>
      </section>

      {/* ===== CATEGORIE ===== */}
      <section className="home-section">
        <div className="section-inner">
          <div className="section-head">
            <h2 className="section-title">Cosa stai cercando?</h2>
            <p className="section-sub">Quattro categorie, infinite possibilità per la tua festa.</p>
          </div>
          <div className="category-grid">
            <Link to="/cerca?tipo=location" className="category-card cat-location">
              <div className="cat-icon"><MapPin size={28} /></div>
              <div className="cat-body">
                <h3>Location</h3>
                <p>Sale, spazi e parchi attrezzati per ogni età.</p>
              </div>
              <ArrowRight className="cat-arrow" size={20} />
            </Link>
            <Link to="/cerca?tipo=animazione" className="category-card cat-animazione">
              <div className="cat-icon"><Sparkles size={28} /></div>
              <div className="cat-body">
                <h3>Intrattenimento</h3>
                <p>Maghi, giocolieri e animatori per stupire i bambini.</p>
              </div>
              <ArrowRight className="cat-arrow" size={20} />
            </Link>
            <Link to="/cerca?tipo=allestimenti" className="category-card cat-allestimenti">
              <div className="cat-icon"><Palette size={28} /></div>
              <div className="cat-body">
                <h3>Allestimenti</h3>
                <p>Decorazioni, palloncini e scenografie personalizzate a tema per la tua festa.</p>
              </div>
              <ArrowRight className="cat-arrow" size={20} />
            </Link>
            <Link to="/cerca?tipo=food" className="category-card cat-food">
              <div className="cat-icon"><Cake size={28} /></div>
              <div className="cat-body">
                <h3>Food</h3>
                <p>Pasticcerie, torte e catering per dolci e buffet della festa.</p>
              </div>
              <ArrowRight className="cat-arrow" size={20} />
            </Link>
          </div>
        </div>
      </section>

      {/* ===== COME FUNZIONA ===== */}
      <section className="home-section alt">
        <div className="section-inner">
          <div className="section-head">
            <h2 className="section-title">Come funziona</h2>
            <p className="section-sub">Organizzare una festa non è mai stato così semplice.</p>
          </div>
          <div className="steps">
            <div className="step">
              <div className="step-num"><SearchIcon size={24} /></div>
              <h4>1. Cerca</h4>
              <p>Filtra per città, prezzo ed età e trova ciò che fa per te.</p>
            </div>
            <div className="step">
              <div className="step-num"><MessageSquare size={24} /></div>
              <h4>2. Contatta</h4>
              <p>Scrivi direttamente al fornitore per chiedere disponibilità e una proposta personalizzata.</p>
            </div>
            <div className="step">
              <div className="step-num"><PartyPopper size={24} /></div>
              <h4>3. Festeggia</h4>
              <p>Prenota e realizza la festa dei sogni dei tuoi bambini.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== SCHEDE IN EVIDENZA ===== */}
      {featured.length > 0 && (
        <section className="home-section">
          <div className="section-inner">
            <div className="section-head row">
              <div>
                <h2 className="section-title">Schede in evidenza</h2>
                <p className="section-sub">Le proposte dei nostri fornitori selezionate per te.</p>
              </div>
              <Link to="/cerca?tipo=location" className="section-link">
                Vedi tutte <ArrowRight size={16} />
              </Link>
            </div>
            <div className="featured-grid">
              {featured.map((s) => (
                <ServiceCard key={s.id} servizio={s} />
              ))}
            </div>
          </div>
        </section>
      )}

      {/* ===== VALUE PROPS ===== */}
      <section className="home-section alt">
        <div className="section-inner">
          <div className="values">
            <div className="value">
              <ShieldCheck size={26} />
              <h4>Fornitori verificati</h4>
              <p>Profili curati con foto, descrizione e dettagli reali.</p>
            </div>
            <div className="value">
              <Image size={26} />
              <h4>Tutto a colpo d'occhio</h4>
              <p>Range di prezzo, attività e zona servita su ogni scheda.</p>
            </div>
            <div className="value">
              <MessageSquare size={26} />
              <h4>Contatto diretto</h4>
              <p>Nessun intermediario: parli subito con chi organizza.</p>
            </div>
          </div>
        </div>
      </section>

      {/* ===== CTA PROVIDER ===== */}
      <section className="provider-cta">
        <div className="provider-cta-inner">
          <div>
            <h2>Sei un professionista delle feste?</h2>
            <p>Crea la tua scheda, è gratis, e raggiungi i genitori della tua zona.</p>
          </div>
          <Link to="/registrazione" className="home-btn home-btn-light">
            Diventa fornitore <ArrowRight size={20} />
          </Link>
        </div>
      </section>
    </div>
  );
}

export default Home;
